import React from 'react';
import cx from 'classnames';
import Touchable, { TouchableProps } from '../Touchable';

import styles from './IconButton.module.scss';
import Tooltip from '../Tooltip';
import { Placement } from '@popperjs/core';

export interface IconButtonProps extends TouchableProps {
  /** accessibilityLabel should provide meaningful context about button to those who rely on a screen reader to interact with the UI */
  accessibilityLabel: string;
  /** Optional custom text for the IconButton's tooltip. If not provided, the accessibilityLabel is used. */
  tooltipText?: string;
  /** If you are applying positioning styles the IconButton directly rather than via an ancestor, use this classname prop to set those positioning attributes, e.g. top, left, right, margin, etc. */
  wrapperClassName?: string;
  /** Apply a className to change the icon color. */
  iconClassName?: string;
  /** The icon to display inside the IconButton */
  icon: React.ReactElement;
  /** Whether or not to hide the IconButton's built-in tooltip, can be dynamically set, as in Modal v2. */
  hideTooltip?: boolean;
  /** Where the tooltip should be placed in relation to its triggering element */
  tooltipPlacement?: Placement;
}

export type Props = IconButtonProps;

export const IconButton: React.FC<IconButtonProps> = ({
  accessibilityLabel,
  tooltipText,
  className,
  wrapperClassName,
  iconClassName,
  icon,
  hideTooltip,
  tooltipPlacement = 'top',
  ...restProps
}) => {
  const isTouchDevice: boolean =
    typeof matchMedia === 'function'
      ? matchMedia('(hover: none)').matches
      : false;

  return (
    <Tooltip
      dark
      placement={tooltipPlacement}
      showOn={isTouchDevice || hideTooltip ? 'disabled' : ['hover', 'focus']}
      small
      target={
        <Touchable
          ariaLabel={accessibilityLabel}
          className={cx(styles.container, className)}
          {...restProps}
        >
          {React.cloneElement(icon, {
            className: cx(styles.icon, iconClassName),
          })}
        </Touchable>
      }
      wrapperClassName={wrapperClassName}
    >
      {tooltipText || accessibilityLabel}
    </Tooltip>
  );
};

IconButton.displayName = 'IconButton';
export default IconButton;
export { styles };
